import { useGetDashboardSummary, useGetSalesChart, useGetTopProducts, useGetExpenseBreakdown, useGetProfitLoss, useListStaff } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, ShoppingCart, AlertTriangle, CreditCard, Package, Users, Receipt, Loader2, ArrowRight, BarChart3, Clock, UserCheck, UserX } from "lucide-react";
import { useAuth } from "@/context/auth";
import { Link } from "wouter";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useBranch } from "@/context/branch";
import { BUSINESS_TYPES, type BusinessType } from "@/lib/business-types";
import { readJsonArray } from "@/lib/http";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "@/pages/dashboards/dashboard-money-visibility";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ── Role guard ────────────────────────────────────────────────────────────────
// Only admin and manager roles receive financial/P&L metrics.
// "staff" (cashier) is restricted to operational counts only.
function useFinancialAccess() {
  const { user } = useAuth();
  const role = user?.role ?? "";
  return role === "admin" || role === "manager";
}

function AttendanceWidget() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeBranchId } = useBranch();
  const { data: staffList = [] } = useListStaff({ branchId: activeBranchId ?? undefined });

  const { data: status = [] } = useQuery({
    queryKey: ["attendance-status", "dashboard", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const response = await fetch(`${BASE}/api/attendance/status${query}`, { credentials: "include" });
      return readJsonArray<any>(response, "attendance status");
    },
    refetchInterval: 60000,
  });

  const clockIn = useMutation({
    mutationFn: async (staffId: number) => {
      const r = await fetch(`${BASE}/api/attendance/clock-in`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ staffId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? "Failed to clock in");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["attendance-status"] }),
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const clockOut = useMutation({
    mutationFn: async (staffId: number) => {
      const r = await fetch(`${BASE}/api/attendance/clock-out`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ staffId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? "Failed to clock out");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["attendance-status"] }),
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const activeStaff = (staffList as any[]).filter((s: any) => s.isActive);
  const clockedInIds = new Set((status as any[]).filter((s: any) => s.isClocked).map((s: any) => s.staffId));
  const clockedInCount = clockedInIds.size;
  const isPending = clockIn.isPending || clockOut.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-500" />
          Today's Attendance
          <span className="text-xs font-normal text-muted-foreground ml-1">
            {clockedInCount} of {activeStaff.length} clocked in
          </span>
        </CardTitle>
        <Link href="/attendance">
          <Button variant="ghost" size="sm" className="gap-1 text-xs">Full Log <ArrowRight className="w-3 h-3" /></Button>
        </Link>
      </CardHeader>
      <CardContent>
        {activeStaff.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center text-sm text-muted-foreground gap-2">
            <Users className="w-8 h-8 opacity-30" />
            <p>No active staff yet.</p>
            <Link href="/staff">
              <Button variant="outline" size="sm">Add Staff</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {activeStaff.map((s: any) => {
              const isIn = clockedInIds.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-3 p-3 border rounded-xl transition-colors ${
                    isIn
                      ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800"
                      : "bg-card"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    isIn ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"
                  }`}>
                    {s.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{s.role}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {isIn
                      ? <UserCheck className="w-4 h-4 text-emerald-500" />
                      : <UserX className="w-4 h-4 text-muted-foreground/50" />
                    }
                    <Button
                      size="sm"
                      variant={isIn ? "outline" : "default"}
                      className={`h-6 text-xs px-2 ${isIn ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : ""}`}
                      onClick={() => isIn ? clockOut.mutate(s.id) : clockIn.mutate(s.id)}
                      disabled={isPending}
                    >
                      {isIn ? "Out" : "In"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function fmt(v: number | undefined | null) {
  if (v == null) return "₦0.00";
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

export default function Dashboard() {
  const { user, store, trialDaysLeft } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const canViewFinancials = useFinancialAccess();
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility(canViewFinancials);
  const formatMoney = (value: number | undefined | null) => maskMoney(fmt(value));
  const branchParams = { branchId: activeBranchId ?? undefined };
  const businessType = user?.businessType as BusinessType | undefined;
  const businessTypeConfig = businessType ? BUSINESS_TYPES[businessType] : undefined;
  const roleLabel = user?.role
    ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}`
    : null;

  const { data: summary, isLoading } = useGetDashboardSummary(branchParams);
  const { data: salesChart }         = useGetSalesChart(branchParams);
  const { data: topProducts }        = useGetTopProducts(branchParams);

  // These queries are completely disabled for staff — no network request is fired,
  // and the hooks return undefined, so nothing leaks through even if the component
  // were re-rendered. The backend also enforces 403 as a second layer.
  const { data: expenseBreakdown } = useGetExpenseBreakdown(
    branchParams,
    { query: { enabled: canViewFinancials } } as any,
  );
  const { data: pl } = useGetProfitLoss(
    { period: "month", ...branchParams },
    { query: { enabled: canViewFinancials } } as any,
  );

  const today = format(new Date(), "EEEE, MMMM d, yyyy");

  return (
    <div className="space-y-6 pb-28">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight">
            Good {getGreeting()}, {user?.name?.split(" ")[0]} 👋
          </h1>
          <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground mt-2">
            <span>{today}</span>
            <span aria-hidden="true">•</span>
            <span className="font-medium text-foreground">{store?.name}</span>
            {businessTypeConfig && (
              <Badge variant="outline" className="rounded-full gap-1 border-primary/30 bg-primary/5 text-primary">
                <span>{businessTypeConfig.emoji}</span>
                <span>{businessTypeConfig.shortLabel}</span>
              </Badge>
            )}
            {roleLabel && (
              <Badge variant="secondary" className="rounded-full">
                {roleLabel}
              </Badge>
            )}
            <span aria-hidden="true">•</span>
            <span>{activeBranch?.name ?? "All Branches"}</span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap shrink-0">
          {canViewFinancials && (
            <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          )}
          {store?.subscriptionStatus === "trial" && (
            <Badge variant="secondary" className="gap-1">
              <span>{trialDaysLeft}d trial left</span>
            </Badge>
          )}
          <Badge className="capitalize bg-primary/10 text-primary border-primary/20 border">
            {store?.plan} plan
          </Badge>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "New Sale",      href: "/sales/new",  icon: ShoppingCart, accent: "border-blue-400/70 text-blue-300 group-hover:border-blue-300" },
          { label: "Add Product",   href: "/inventory",  icon: Package,      accent: "border-emerald-400/70 text-emerald-300 group-hover:border-emerald-300" },
          { label: "New Purchase",  href: "/purchases",  icon: Receipt,      accent: "border-violet-400/70 text-violet-300 group-hover:border-violet-300" },
          { label: "View Reports",  href: "/reports",    icon: BarChart3,    accent: "border-amber-400/70 text-amber-300 group-hover:border-amber-300" },
        ].map(a => (
          <Link key={a.label} href={a.href}>
            <div className={`group flex items-center gap-2.5 px-4 py-3.5 rounded-xl cursor-pointer transition-colors font-medium text-sm bg-[#0D1B2A] border ${a.accent} hover:bg-[#13263A]`}>
              <a.icon className="w-4 h-4 flex-shrink-0" />
              <span className="text-white">{a.label}</span>
            </div>
          </Link>
        ))}
      </div>

      {/* KPI Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* ── Operational KPIs — visible to all roles ─────────────────────── */}
          <div className={`grid gap-4 sm:grid-cols-2 ${canViewFinancials ? "lg:grid-cols-5" : "lg:grid-cols-3"}`}>

            {/* Today's Revenue — admin/manager only */}
            {canViewFinancials && (
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">Today's Revenue</CardTitle>
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatMoney(summary?.todayRevenue)}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    This month: <span className="font-medium text-foreground">{formatMoney(summary?.monthlyRevenue)}</span>
                  </p>
                </CardContent>
              </Card>
            )}

            {/* VAT is shown to the same roles as other financial KPIs and masked by the shared toggle. */}
            {canViewFinancials && (
              <Card className="border-l-4 border-l-amber-500">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">VAT Collected This Month</CardTitle>
                  <Receipt className="w-4 h-4 text-amber-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatMoney(summary?.monthlyVatCollected)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Net of processed returns</p>
                </CardContent>
              </Card>
            )}

            {/* Transactions — all roles */}
            <Card className="border-l-4 border-l-emerald-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Transactions</CardTitle>
                <CreditCard className="w-4 h-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.todayTransactions ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">Today</p>
              </CardContent>
            </Card>

            {/* Total Products — all roles */}
            <Card className="border-l-4 border-l-violet-500">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                <Package className="w-4 h-4 text-violet-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalProducts ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">In inventory</p>
              </CardContent>
            </Card>

            {/* Low Stock — all roles */}
            <Card className="border-l-4 border-l-red-400">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
                <AlertTriangle className="w-4 h-4 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{summary?.lowStockCount ?? 0}</div>
                <p className="text-xs text-muted-foreground mt-1">Items below reorder</p>
              </CardContent>
            </Card>
          </div>

          {/* ── P&L Quick Summary — admin/manager only ───────────────────────── */}
          {canViewFinancials && pl && (
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2 space-y-0">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">This Month's Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-primary">{formatMoney(pl.revenue)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2 space-y-0">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Gross Profit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold text-emerald-600">{formatMoney(pl.grossProfit)}</div>
                  <p className="text-xs text-muted-foreground">{pl.grossMargin}% margin</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2 space-y-0">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Net Profit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-xl font-bold ${(pl.netProfit ?? 0) < 0 ? "text-destructive" : "text-emerald-600"}`}>
                    {formatMoney(pl.netProfit)}
                  </div>
                  <p className="text-xs text-muted-foreground">{pl.netMargin}% net margin</p>
                  {(pl as any).returnRevenueSnapshotsComplete === false && (
                    <p className="text-xs text-amber-600 mt-1">Some older returns lack a stored net-revenue allocation; their totals retain legacy treatment.</p>
                  )}
                  {(pl as any).returnCostSnapshotsComplete === false && (
                    <p className="text-xs text-amber-600 mt-1">Some historical returns use current product cost because the original cost was not recorded.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Charts row ───────────────────────────────────────────────────── */}
          <div className="grid gap-6 lg:grid-cols-7">

            {/* Sales Trend — revenue axis shown only to admin/manager */}
            <Card className="lg:col-span-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Sales Trend (30 days)</CardTitle>
                <Link href="/reports">
                  <Button variant="ghost" size="sm" className="gap-1 text-xs">View Reports <ArrowRight className="w-3 h-3" /></Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={salesChart as any[]}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      {/* Staff see transaction counts; admins/managers see ₦ revenue */}
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={canViewFinancials
                          ? moneyVisible ? (v: number) => `₦${(v / 1000).toFixed(0)}k` : () => "####"
                          : (v: number) => String(v)
                        }
                      />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                        formatter={canViewFinancials
                          ? moneyVisible ? (v: number) => [`₦${v.toLocaleString("en-NG")}`, "Revenue"] : () => ["####", "Revenue"]
                          : (v: number) => [v, "Transactions"]
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey={canViewFinancials ? "revenue" : "transactions"}
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Products — revenue column shown only to admin/manager */}
            <Card className="lg:col-span-3">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Top Products</CardTitle>
                <Link href="/inventory">
                  <Button variant="ghost" size="sm" className="gap-1 text-xs">Inventory <ArrowRight className="w-3 h-3" /></Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(topProducts as any[])?.slice(0, 6).map((product: any, i: number) => (
                    <div key={product.productId} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.productName}</p>
                        <p className="text-xs text-muted-foreground">{product.totalSold} sold</p>
                      </div>
                      {canViewFinancials && (
                        <div className="text-sm font-semibold">{formatMoney(product.totalRevenue)}</div>
                      )}
                    </div>
                  ))}
                  {!topProducts?.length && (
                    <div className="text-sm text-muted-foreground text-center py-8">
                      <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      No sales data yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Bottom row ───────────────────────────────────────────────────── */}
          <div className="grid gap-6 md:grid-cols-2">

            {/* Expense Breakdown — admin/manager only; completely absent from DOM for staff */}
            {canViewFinancials ? (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Expense Breakdown</CardTitle>
                  <Link href="/expenses">
                    <Button variant="ghost" size="sm" className="gap-1 text-xs">View All <ArrowRight className="w-3 h-3" /></Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {expenseBreakdown?.length ? (
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={expenseBreakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="total" nameKey="category">
                            {expenseBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                            formatter={(v: number) => [formatMoney(v), ""]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
                        {expenseBreakdown.slice(0, 5).map((e: any, i: number) => (
                          <div key={e.category} className="flex items-center gap-1.5 text-xs">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            {e.category}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center">
                      <div className="text-center text-muted-foreground text-sm">
                        <Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        No expenses recorded yet
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Staff replacement: show today's transaction summary instead */
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-emerald-500" />
                    Today's Activity
                  </CardTitle>
                  <Link href="/sales">
                    <Button variant="ghost" size="sm" className="gap-1 text-xs">View Sales <ArrowRight className="w-3 h-3" /></Button>
                  </Link>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-100 dark:border-emerald-900">
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Transactions processed</span>
                    <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{summary?.todayTransactions ?? 0}</span>
                  </div>
                  <Link href="/sales/new">
                    <Button className="w-full gap-2" variant="default">
                      <ShoppingCart className="w-4 h-4" /> Start a New Sale
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Low stock alerts — all roles */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Low Stock Alerts
                  {(summary?.lowStockCount ?? 0) > 0 && <Badge variant="destructive" className="text-xs">{summary?.lowStockCount}</Badge>}
                </CardTitle>
                <Link href="/inventory?lowStock=true">
                  <Button variant="ghost" size="sm" className="gap-1 text-xs">View All <ArrowRight className="w-3 h-3" /></Button>
                </Link>
              </CardHeader>
              <CardContent>
                {(summary?.lowStockCount ?? 0) === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-3">
                      <Package className="w-6 h-6 text-emerald-500" />
                    </div>
                    <p className="text-sm font-medium text-emerald-700">All stock levels are healthy</p>
                    <p className="text-xs text-muted-foreground mt-1">No products are below reorder level</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground mb-3">
                      <strong className="text-destructive">{summary?.lowStockCount}</strong> product{summary!.lowStockCount > 1 ? "s" : ""} need restocking
                    </p>
                    <Link href="/inventory?lowStock=true">
                      <Button variant="destructive" size="sm" className="w-full gap-2">
                        <AlertTriangle className="w-4 h-4" /> Review Low Stock Items
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Attendance / Clock-In Board */}
          {(store?.plan === "enterprise" || store?.subscriptionStatus === "trial") && (
            <AttendanceWidget />
          )}
        </>
      )}
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}
